import { Box, Typography, useTheme } from '@mui/material';
import { IconAlertTriangle } from '@tabler/icons-react';

/**
 * v4 danger zone (DESIGN_SYSTEM.md section 8.5, #1377). Destructive-action
 * container, board look: raised surface with a color-mixed negative border, a
 * negative dot header, an amber alert triangle + explanatory message, and an
 * outlined (never filled) red action button. One per page, always last.
 *
 * Pass `title={null}` to hide the header when the page supplies an off-card
 * section label (desktop Account). Either pass `actionLabel` + `onAction` for
 * the standard outlined button, or compose your own controls via `children`.
 *
 * Theme-native: negative border/button via color-mix on error.main, alert icon
 * warning.main. No hardcoded hex.
 *
 * @param {object} props
 * @param {React.ReactNode|null} [props.title]  header label (default "Danger zone"; null hides)
 * @param {React.ReactNode} [props.message]     alert explanation text
 * @param {React.ReactNode} [props.icon]        alert icon (default Tabler alert-triangle)
 * @param {React.ReactNode} [props.actionLabel] outlined destructive button label
 * @param {function} [props.onAction]           destructive button handler
 * @param {boolean} [props.fullWidthButton]     full-width button (mobile)
 * @param {('default'|'compact')} [props.density] shell padding density
 * @param {React.ReactNode} [props.children]    custom destructive action(s)
 * @param {object} [props.sx]                   extra sx merged onto the zone
 */
const DangerZone = ({
  title = 'Danger zone',
  message,
  icon,
  actionLabel,
  onAction,
  fullWidthButton = false,
  density = 'compact',
  children,
  sx,
  ...rest
}) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const msg = message;

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: palette.surface.raised,
        border: `1px solid color-mix(in srgb, ${palette.error.main} 30%, transparent)`,
        borderRadius: `${ds.radius.container}px`,
        padding: density === 'compact' ? '2px 14px' : '2px 16px',
        transition: `background-color ${ds.motion.base} ${ds.motion.easeOut}, border-color ${ds.motion.base} ${ds.motion.easeOut}`,
        ...sx,
      }}
      {...rest}
    >
      {title != null && (
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            padding: '9px 0 8px',
            borderBottom: `1px solid ${palette.divider}`,
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: `${ds.space[2]}px`,
              color: palette.error.main,
              fontSize: `${ds.font.meta}px`,
              fontWeight: ds.font.weightMedium,
            }}
          >
            <Box
              component="span"
              sx={{
                width: ds.size.dot,
                height: ds.size.dot,
                borderRadius: '50%',
                backgroundColor: palette.error.main,
                flexShrink: 0,
              }}
            />
            <span>{title}</span>
          </Box>
        </Box>
      )}

      {msg != null && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px', padding: '10px 0 4px' }}>
          <Box
            component="span"
            sx={{
              color: palette.warning.main,
              display: 'flex',
              flexShrink: 0,
              marginTop: '1px',
              '& svg': { width: 15, height: 15 },
            }}
          >
            {icon || <IconAlertTriangle />}
          </Box>
          <Typography
            component="span"
            sx={{ fontSize: `${ds.font.dim}px`, lineHeight: 1.5, color: palette.text.secondary }}
          >
            {msg}
          </Typography>
        </Box>
      )}

      {children}

      {actionLabel != null && (
        <Box
          component="button"
          type="button"
          onClick={onAction}
          sx={{
            width: fullWidthButton ? '100%' : 'auto',
            background: 'none',
            border: `1px solid color-mix(in srgb, ${palette.error.main} 50%, transparent)`,
            color: palette.error.main,
            borderRadius: `${ds.radius.button}px`,
            padding: fullWidthButton ? '10px 0' : '8px 14px',
            fontSize: '13px',
            fontWeight: ds.font.weightMedium,
            fontFamily: 'inherit',
            margin: '8px 0 10px',
            cursor: 'pointer',
          }}
        >
          {actionLabel}
        </Box>
      )}
    </Box>
  );
};

export default DangerZone;
